import { createContext, useCallback, useContext, useMemo, useState } from "react";
import en from "./local/en/common";
import ko from "./local/ko/common";
import zh from "./local/zh/common";

type Language = "en" | "ko" | "zh";

const resources: Record<Language, Record<string, string>> = { en, ko, zh };
const languages: Language[] = ["en", "ko", "zh"];

type TranslationContextValue = {
  language: Language;
  changeLanguage: (nextLanguage: string) => void;
  t: (key: string, fallback?: string) => string;
};

const TranslationContext = createContext<TranslationContextValue | null>(null);

function getInitialLanguage(): Language {
  if (typeof window === "undefined") return "ko";
  const stored = window.localStorage.getItem("dragonfruit-language");
  if (stored && languages.includes(stored as Language)) return stored as Language;
  const browserLanguage = window.navigator.language.toLowerCase();
  if (browserLanguage.startsWith("ko")) return "ko";
  if (browserLanguage.startsWith("zh")) return "zh";
  return "en";
}

export function TranslationProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  const changeLanguage = useCallback((nextLanguage: string) => {
    const normalized = languages.includes(nextLanguage as Language)
      ? (nextLanguage as Language)
      : "en";
    setLanguage(normalized);
    window.localStorage.setItem("dragonfruit-language", normalized);
    document.documentElement.lang = normalized;
  }, []);

  const t = useCallback(
    (key: string, fallback?: string) => {
      return resources[language][key] || resources.en[key] || fallback || key;
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, changeLanguage, t }),
    [changeLanguage, language, t]
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used inside TranslationProvider");
  }
  return {
    t: context.t,
    i18n: {
      language: context.language,
      changeLanguage: context.changeLanguage,
    },
  };
}
